import { useId, useState } from "react";

import { QueryPendingState } from "@/api/QueryPendingState";
import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  classifySummaryPolicyWriteFailure,
  useReplaceSummaryPolicyMutation,
  useSummaryPolicyQuery,
  type ProjectSummaryPolicy,
  type SummaryPolicyState,
  type SummaryPolicyWriteFailure,
} from "@/api/summaryPolicy";
import { useCapability } from "@/auth/SessionContext";
import { formatDateTime } from "@/lib/format";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Field, Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { StaleDataNotice } from "@/ui/states";

/**
 * Clinical generation policy — the Project's post-generation decision (#864).
 *
 * ## WHY IT IS A CARD OF ITS OWN, BESIDE THE COMPOSER AND NOT INSIDE IT
 *
 * The [Lovable Project Summary
 * screen](https://noproductcontent.lovable.app/projects/mod/config/summary) is
 * a Summary composer — font, base size, and twelve draggable section rows with
 * a visibility eye — and this ticket preserves it untouched. What it has no
 * expression for is a CLINICAL CONTENT decision: whether the engine appends
 * secondary diagnostic indicators to the chief complaint after the summary is
 * generated. That is the explicit addition, and it is a separate card because
 * folding it into the composer would say the opposite of what is true.
 *
 * THE TWO ARE DIFFERENT LAYERS, and conflating them is the specific defect this
 * ticket exists to prevent:
 *
 *  - A section's visibility decides whether a produced section is DISPLAYED in
 *    the report. It is target-renderer state, it writes
 *    `/summary-presentation`, and it cannot change what the engine produces.
 *  - This policy decides whether one sentence is PRODUCED at all. The pinned
 *    Brain builds the summary and then, in
 *    `summary_agent/routes.py::_append_secondary_diagnostic_indicators`, reads
 *    this flag; when it is on and the conversation raised conditions that
 *    scored at or above the indicator threshold without being evaluated, it
 *    appends one sentence naming them to the chief complaint. The sentence
 *    lands INSIDE a section, so no visibility switch can remove it — and it is
 *    not the model's to write, so no prompt wording can either.
 *
 * The copy says that where the decision is made, because the plausible
 * misreading of a control on a screen full of visibility switches is that this
 * one hides something.
 *
 * ## THREE STATES, AND THE THIRD IS NOT A THIRD VALUE
 *
 * `Inherited` is the absence of a decision — the base pack's value applies and
 * the Project's candidate carries no `summary_agent/config.yaml` at all. That
 * is what lets one Project turn the policy off without any other Project
 * copying that choice, and it is offered as a first-class option rather than
 * as "clear", because a clinician reading the card has to be able to see which
 * of the three this Project is in.
 *
 * ## WHAT IT WILL NOT OFFER
 *
 * No control reaches any other `summary_agent/config.yaml` value: the
 * generation parameters are named as read-only text from the server's own
 * `protectedFields`. Where the account cannot write, the select and Save are
 * disabled with the server's own reason rather than hidden — the state is part
 * of what the Project IS, and a reviewer must be able to read it.
 */
export function SummaryGenerationPolicy({ projectId }: { projectId: string }) {
  const stateQuery = useSummaryPolicyQuery(projectId);
  const terminalFailure =
    stateQuery.isError && isTerminalQueryError(stateQuery.error);
  const state = terminalFailure ? undefined : stateQuery.data;

  return (
    // `aria-label` rather than a visible `<h3>`, unlike
    // `SummaryPresentationControls` next door. That panel's heading
    // ("Presentation and rendering") names something narrower than its card
    // ("Summary report structure"); this section IS its card, so a heading
    // would render the same words twice — the repetition #909 removed from
    // this screen's other panel. The accessible name is kept so the region is
    // still addressable, including when the component is rendered alone.
    <section className="space-y-3" aria-label="Clinical generation policy">
      <p className="text-sm text-muted-foreground">
        What the clinical engine adds to the summary after it is generated.
        These settings change the content of the report, not how it is laid out
        — section order and visibility are separate, below.
      </p>

      {stateQuery.isPending ? (
        <QueryPendingState
          isPaused={stateQuery.isPaused}
          failureReason={stateQuery.failureReason}
          label="Loading this Project's clinical generation policy"
          errorTitle="The clinical generation policy could not be loaded"
          rows={2}
          queryKey={queryKeys.projectSummaryPolicy(projectId)}
        />
      ) : state === undefined ? (
        <QueryErrorState
          error={stateQuery.error}
          title="The clinical generation policy could not be loaded"
          subject="Clinical generation policies"
          onRetry={() => void stateQuery.refetch()}
        />
      ) : (
        <>
          {stateQuery.isError ? (
            <StaleDataNotice
              updatedAt={stateQuery.dataUpdatedAt}
              onRetry={() => void stateQuery.refetch()}
            />
          ) : null}
          {/*
            KEYED ON THE PROJECT, and it is load-bearing rather than tidy.
            The editor below seeds `baseline`, `choice` and `statusMessage`
            from props with `useState`, which runs only on mount — so if this
            stays mounted while `projectId` changes, it keeps the PREVIOUS
            Project's baseline and selection. `remoteMoved` would then compare
            the new Project's version against the old one's, and Save would put
            the old Project's choice behind the old Project's
            `expectedPolicyVersion` at the NEW Project's endpoint: a
            cross-Project write whenever the two counters happened to agree.

            A `key` is the reset, not a `useEffect` that copies props into
            state: React discards the subtree and every piece of local editor
            state with it, so a field added later cannot forget to be cleared.
          */}
          <SecondaryIndicatorPolicy
            key={projectId}
            projectId={projectId}
            state={state}
            refetch={stateQuery.refetch}
          />
        </>
      )}
    </section>
  );
}

const OPTION_LABEL: Record<SummaryPolicyState, string> = {
  inherited: "Inherited from the base pack",
  enabled: "On — append them",
  disabled: "Off — do not append them",
};

function SecondaryIndicatorPolicy({
  projectId,
  state,
  refetch,
}: {
  projectId: string;
  state: ProjectSummaryPolicy;
  refetch: ReturnType<typeof useSummaryPolicyQuery>["refetch"];
}) {
  const write = useCapability("draft.write");
  const mutation = useReplaceSummaryPolicyMutation(projectId);
  const permissionId = useId();
  const [baseline, setBaseline] = useState(state);
  const [choice, setChoice] = useState<SummaryPolicyState>(state.state);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const dirty = choice !== baseline.state;
  const remoteMoved = state.policyVersion !== baseline.policyVersion;
  const failure = mutation.isError
    ? classifySummaryPolicyWriteFailure(mutation.error)
    : null;
  const unresolvedFailure =
    failure !== null &&
    (failure.kind === "stale" || failure.kind === "ambiguous");
  const editingBlocked =
    !write.allowed || mutation.isPending || remoteMoved || unresolvedFailure;
  const blockingReason = !write.allowed
    ? write.reason
    : mutation.isPending
      ? "Saving this Project's generation policy."
      : remoteMoved || unresolvedFailure
        ? "The server state must be reloaded before this policy can be changed."
        : null;

  /**
   * The recovery out of every blocked state — `SummaryPresentationControls`'
   * `reloadLatest`, for the reason ADR 0001 gives.
   *
   * `baseline` and `choice` are seeded once, so a background refetch that
   * observes another writer's newer version raises `remoteMoved` and disables
   * editing FOREVER: nothing else on this surface replaces the baseline, and
   * the blocking sentence says the state must be reloaded while offering no
   * way to reload it. That is the dead control the ADR forbids, and it needed
   * a full browser remount to escape.
   *
   * The local choice is discarded rather than rebased onto the new version:
   * this control holds one state, and silently re-pointing an unsaved
   * selection at a baseline the clinician has not seen is how a lost update
   * gets written under a token that looks current.
   */
  async function reloadLatest() {
    const result = await refetch();
    if (result.data !== undefined && !result.isError) {
      setBaseline(result.data);
      setChoice(result.data.state);
      mutation.reset();
      setStatusMessage(
        `Loaded Project summary-policy version ${result.data.policyVersion}; the previous local selection was discarded.`,
      );
    }
  }

  async function save() {
    if (editingBlocked || !dirty) return;
    setStatusMessage(null);
    try {
      const saved = await mutation.mutateAsync({
        expectedPolicyVersion: baseline.policyVersion,
        state: choice,
      });
      setBaseline(saved);
      setChoice(saved.state);
      setStatusMessage(
        `Clinical generation policy saved as Project version ${saved.policyVersion}.`,
      );
    } catch {
      // The typed failure below renders it. `mutateAsync` would otherwise
      // reject unhandled, which is what surfaces as an unhandled rejection in
      // a component that DISPLAYS the refusal.
    }
  }

  return (
    <div className="space-y-4" data-slot="summary-generation-policy">
      {/*
        The remote-moved recovery, ahead of the row so it is the first thing
        read once editing is blocked. Suppressed for a `stale` write refusal,
        which renders its own reload below and would otherwise say the same
        thing twice — `SummaryPresentationControls`' condition exactly.
      */}
      {remoteMoved && failure?.kind !== "stale" ? (
        <Notice
          role="alert"
          tone="destructive"
          action={
            <Button variant="outline" onClick={() => void reloadLatest()}>
              Reload latest
            </Button>
          }
        >
          Another save produced Project summary-policy version{" "}
          {state.policyVersion}. Your unsaved selection is still here and has
          not been submitted over it.
        </Notice>
      ) : null}

      <div className="rounded-md border border-border bg-card px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0 flex-1 space-y-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                Include secondary diagnostic indicators
              </span>
              <Badge tone={state.state === "inherited" ? "outline" : "accent"}>
                {state.state === "inherited"
                  ? "Inherited"
                  : "Set by this Project"}
              </Badge>
            </span>
            <p className="text-sm text-muted-foreground">
              After the summary is generated, the clinical engine can add one
              sentence to the chief complaint naming conditions the
              conversation raised strongly but never evaluated. Turning this
              off skips that step, so the sentence is never written. It does
              not hide a report section, and hiding a section does not turn it
              off.
            </p>
            <p className="text-sm text-foreground">{effectiveSentence(state)}</p>
          </div>

          <div className="w-full max-w-xs shrink-0">
            <Field label="This Project">
              {({ inputId, describedBy }) => (
                <Select
                  id={inputId}
                  aria-describedby={describedBy}
                  value={choice}
                  // `aria-disabled` and not the native attribute, and the
                  // change is refused in the handler instead —
                  // `SummaryPresentationControls`' idiom, for `Button.tsx`'s
                  // stated reason: a natively disabled control leaves the tab
                  // order, which hides the reason from exactly the users who
                  // most need to read it.
                  aria-disabled={editingBlocked || undefined}
                  onChange={(event) => {
                    if (editingBlocked) return;
                    setChoice(event.target.value as SummaryPolicyState);
                    mutation.reset();
                    setStatusMessage(null);
                  }}
                >
                  {(
                    ["inherited", "enabled", "disabled"] as const
                  ).map((option) => (
                    <option key={option} value={option}>
                      {OPTION_LABEL[option]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            onClick={() => void save()}
            disabled={editingBlocked || !dirty}
            aria-describedby={!write.allowed ? permissionId : undefined}
          >
            {mutation.isPending ? "Saving…" : "Save policy"}
          </Button>
          {dirty ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setChoice(baseline.state);
                mutation.reset();
                setStatusMessage(null);
              }}
              disabled={mutation.isPending}
            >
              Discard
            </Button>
          ) : null}
          <span className="text-xs text-muted-foreground">
            {protectedSentence(state)}
          </span>
        </div>

        {blockingReason ? (
          <p id={permissionId} className="mt-3 text-xs text-muted-foreground">
            {blockingReason}
          </p>
        ) : null}

        {state.updatedAt ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Last changed {formatDateTime(state.updatedAt)} · Project version{" "}
            {state.policyVersion}
          </p>
        ) : null}
      </div>

      {state.readRefusal === "base_unavailable" ? (
        <Notice tone="warning">
          This deployment could not read the base pack, so what inheriting would
          do is unknown. Setting this policy on or off still works — the
          decision does not depend on the base value.
        </Notice>
      ) : null}

      {/*
        `SummaryPresentationControls`' idiom: the visible confirmation of a
        saved policy is the card itself — the badge, the effective sentence and
        the version line all move — so the announcement is a live region rather
        than a second banner repeating what the reader can already see. A
        `Notice` would have to borrow the warning tone, which is the wrong
        thing to say about a successful save.
      */}
      <p role="status" aria-live="polite" className="sr-only">
        {statusMessage}
      </p>

      {failure ? (
        <PolicyWriteFailure
          failure={failure}
          onReload={() => void reloadLatest()}
        />
      ) : null}
    </div>
  );
}

/** What the runtime will actually do, in one sentence with its provenance. */
function effectiveSentence(state: ProjectSummaryPolicy): string {
  const effective = state.effectiveIncludeSecondaryDiagnosticIndicators;
  if (effective === undefined || effective === null) {
    return "Effective setting unknown: the base pack could not be read and this Project has not decided.";
  }
  const verb = effective
    ? "Secondary diagnostic indicators are appended"
    : "Secondary diagnostic indicators are not appended";
  return state.source === "project_override"
    ? `${verb}, because this Project decided it.`
    : `${verb}, inherited from the base pack.`;
}

function protectedSentence(state: ProjectSummaryPolicy): string {
  const protectedFields = state.protectedFields ?? [];
  if (protectedFields.length === 0) return "";
  return `Every other summary generation setting (${protectedFields.join(", ")}) stays inherited from the base pack.`;
}

function PolicyWriteFailure({
  failure,
  onReload,
}: {
  failure: SummaryPolicyWriteFailure;
  /** The one recovery out of the two refusals that block further editing. */
  onReload: () => void;
}) {
  const reload = (
    <Button variant="outline" onClick={onReload}>
      Reload latest
    </Button>
  );

  switch (failure.kind) {
    case "stale":
      return (
        <Notice tone="warning" role="alert" action={reload}>
          Somebody else changed this Project&apos;s generation policy
          {failure.currentPolicyVersion === null
            ? ""
            : ` (now version ${failure.currentPolicyVersion})`}
          . Nothing was saved — reload the stored policy and reapply your
          change.
        </Notice>
      );
    case "denied":
      return (
        <Notice tone="warning" role="alert">
          This account may no longer change this Project&apos;s configuration.
          Nothing was saved.
        </Notice>
      );
    case "upstream":
      return (
        <Notice tone="warning" role="alert">
          The service could not complete the change. Nothing was saved; try
          again.
        </Notice>
      );
    case "invalid":
      return (
        <Notice tone="warning" role="alert">
          {failure.message}
        </Notice>
      );
    case "ambiguous":
      return (
        <Notice tone="warning" role="alert" action={reload}>
          The service did not confirm the change, so it may or may not have been
          saved. Reload the stored policy to see which before trying again
          {failure.requestId === undefined
            ? ""
            : ` (request ${failure.requestId})`}
          .
        </Notice>
      );
    default:
      return (
        <Notice tone="warning" role="alert">
          The policy could not be saved ({failure.code})
          {failure.requestId === undefined
            ? ""
            : ` (request ${failure.requestId})`}
          .
        </Notice>
      );
  }
}
